import { useHostType } from '@repo/host/hooks/use-host-info';
import { getHost } from '@repo/host/host';
import type { PreferencesClientMessage } from '@repo/map-preferences/types';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectFollowEnabled, selectLocationEnabled, selectPlayerBearing, selectPlayerCoords, selectPlayerLocationDeltas, selectTeleportEnabled, selectTitleId, selectTitleSlug, setFollowEnabled, setLocationEnabled, setPlayerBearing, setPlayerCoords, setTeleportEnabled, setTrainerConnected, toggleFollowEnabled } from '@repo/store/maps/slices/mapData';
import { HostType } from '@repo/types/host';
import type { MapDataGameCoordinates } from '@repo/types/maps/map-types';
import { calculateBearing, gameCoordsToLatLng, gameDirectionToBearing, remapGameCoords } from '@repo/types/maps/map-utils';
import { TrainerFlags } from '@repo/types/trainers';
import { hasFlag } from '@repo/utility/flags';
import type * as MapLibre from 'maplibre-gl';
import { type MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { tarkovTitleSlug } from '../../constants/escape-from-tarkov';
import { fogDebugOnce } from '../../fog-of-war/fog-debug';
import type { FogRevealAtLatLngOptions } from '../../fog-of-war/useFogOfWarSampler';
import { useMapContext } from '../map-context';
import { useMapControls } from './useMapControls';
const MIN_DIRECTION_DISTANCE_M = 10;
const MIN_WS_POSITION_DISTANCE = 5;
const MIN_WS_BEARING_CHANGE = 10;
const CONTRACT_HEADING_FRESHNESS_MS = 2_500;
const HOST_MOVEMENT_HEADING_FRESHNESS_MS = 2_500;
const PLAYER_POSITION_HEARTBEAT_MS = 5_000;
export const UPDATE_INTERVAL_MS = 1000;
interface UsePlayerLocationOptions {
  lockToPlayer?: boolean;
}
export function usePlayerLocation(sendMessageRef?: MutableRefObject<((message: PreferencesClientMessage) => void) | null>, fogRevealAtLatLng?: (latLng: MapLibre.LngLat, options?: FogRevealAtLatLngOptions) => void, options?: UsePlayerLocationOptions) {
  const dispatch = useAppDispatch();
  const hostType = useHostType();
  const {
    map
  } = useMapContext();
  const followEnabled = useAppSelector(selectFollowEnabled);
  const locationEnabled = useAppSelector(selectLocationEnabled);
  const teleportEnabled = useAppSelector(selectTeleportEnabled);
  const playerCoords = useAppSelector(selectPlayerCoords);
  const playerBearing = useAppSelector(selectPlayerBearing);
  const playerLocationDeltas = useAppSelector(selectPlayerLocationDeltas);
  const titleId = useAppSelector(selectTitleId);
  const titleSlug = useAppSelector(selectTitleSlug);
  const lockToPlayer = options?.lockToPlayer ?? false;
  const shouldFollowPlayer = lockToPlayer || followEnabled;
  const prevLatLngRef = useRef<MapLibre.LngLat | null>(null);
  const playerBearingRef = useRef(playerBearing);
  const lastSentCoordsRef = useRef<{
    x: number;
    y: number;
    z: number;
  } | null>(null);
  const lastSentBearingRef = useRef<number | null>(null);
  const lastSentAtRef = useRef(0);
  const cameraBearingRef = useRef<number | null>(null);
  const cameraBearingUpdatedAtRef = useRef(0);
  const hostMovementBearingRef = useRef<{
    bearing: number;
    updatedAt: number;
  } | null>(null);
  const {
    flyTo
  } = useMapControls();
  playerBearingRef.current = playerBearing;
  const deltas = useMemo(() => {
    return playerLocationDeltas;
  }, [playerLocationDeltas]);
  const setCoords = useCallback((coords: MapDataGameCoordinates) => {
    dispatch(setPlayerCoords(coords));
  }, [dispatch]);
  const setBearing = useCallback((bearing: number | null) => {
    if (playerBearingRef.current === bearing) return;
    playerBearingRef.current = bearing;
    dispatch(setPlayerBearing(bearing));
  }, [dispatch]);
  const enableLocation = useCallback(() => {
    dispatch(setLocationEnabled(true));
  }, [dispatch]);
  const disableLocation = useCallback(() => {
    dispatch(setLocationEnabled(false));
  }, [dispatch]);
  const enableTeleport = useCallback(() => {
    dispatch(setTeleportEnabled(true));
  }, [dispatch]);
  const disableTeleport = useCallback(() => {
    dispatch(setTeleportEnabled(false));
  }, [dispatch]);
  const toggleFollow = useCallback(() => {
    dispatch(toggleFollowEnabled());
  }, [dispatch]);
  const setFollow = useCallback((enabled: boolean) => {
    dispatch(setFollowEnabled(enabled));
  }, [dispatch]);
  const centerPlayer = useCallback((latLng: MapLibre.LngLat) => {
    if (lockToPlayer) {
      map?.setCenter(latLng);
      return;
    }
    flyTo({
      center: latLng,
      duration: 500,
      easing: t => t
    });
  }, [flyTo, lockToPlayer, map]);
  useEffect(() => {
    const subscription = getHost().onCurrentTrainer(info => {
      const connected = info?.titleId === titleId;
      dispatch(setTrainerConnected(connected));
      if (hostType === HostType.APP) {
        sendMessageRef?.current?.({
          type: 'trainer_presence',
          connected,
          miniMapOpenHandler: true
        });
      }
      if (!info || !connected) {
        disableLocation();
        disableTeleport();
        return;
      }
      if (hasFlag(info.flags, TrainerFlags.SupportsTeleporting)) {
        enableTeleport();
      }
    });
    return () => subscription.dispose();
  }, [disableLocation, disableTeleport, dispatch, enableTeleport, hostType, sendMessageRef, titleId]);
  useEffect(() => {
    if (!map || deltas) return;
    fogDebugOnce('no-player-location-deltas', 'Map metadata is missing playerLocationDeltas — live player position and fog reveals stay off. Panning the map alone does not reveal fog (reveals follow live coords).');
  }, [map, deltas]);
  useEffect(() => {
    const subscription_0 = getHost().onPlayerLocationUpdate(message => {
      const cameraBearing = message.cameraFacingDirection && deltas ? gameDirectionToBearing(message.cameraFacingDirection, deltas.deltas) : null;
      cameraBearingRef.current = cameraBearing;
      cameraBearingUpdatedAtRef.current = Date.now();
      if (cameraBearing !== null) setBearing(cameraBearing);
    });
    return () => subscription_0.dispose();
  }, [deltas, setBearing]);
  useEffect(() => {
    const subscription_1 = getHost().onPlayerMovementBearing(bearing_0 => {
      hostMovementBearingRef.current = bearing_0 === null ? null : {
        bearing: bearing_0,
        updatedAt: Date.now()
      };
      if (bearing_0 !== null) setBearing(bearing_0);
    });
    return () => subscription_1.dispose();
  }, [setBearing]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: sendMessageRef is a stable ref — accessing .current inside the effect is intentional
  useEffect(() => {
    if (!map || !deltas) return;
    const poll = async () => {
      const trainerCoords = await getHost().getPlayerCoordinates();
      if (!trainerCoords || Number.isNaN(trainerCoords.x)) return;
      const coords_0 = remapGameCoords(trainerCoords, deltas?.deltas.gameCoordsMap);
      const latLng_0 = gameCoordsToLatLng(coords_0, deltas?.deltas);
      if (!latLng_0) return;
      setCoords(coords_0);

      // TODO: Look into moving this into the desktop app to
      // not send events for live location if we don't want them displayed.
      if (titleSlug === tarkovTitleSlug) {
        const matchType = coords_0.metadata?.matchType;
        if (matchType && matchType === 'local') {
          enableLocation();
          fogRevealAtLatLng?.(latLng_0, {
            trustLivePosition: true
          });
        } else {
          disableLocation();
        }
      } else {
        enableLocation();
        fogRevealAtLatLng?.(latLng_0, {
          trustLivePosition: true
        });
      }
      const prev = prevLatLngRef.current;
      const movementBearing = prev && prev.distanceTo(latLng_0) > MIN_DIRECTION_DISTANCE_M ? calculateBearing(prev, latLng_0) : null;
      const cameraBearing_0 = Date.now() - cameraBearingUpdatedAtRef.current < CONTRACT_HEADING_FRESHNESS_MS ? cameraBearingRef.current : null;
      const hostMovementBearing = hostMovementBearingRef.current && Date.now() - hostMovementBearingRef.current.updatedAt < HOST_MOVEMENT_HEADING_FRESHNESS_MS ? hostMovementBearingRef.current.bearing : null;
      const currentBearing = cameraBearing_0 ?? hostMovementBearing ?? movementBearing;
      if (prev) setBearing(currentBearing);
      prevLatLngRef.current = latLng_0;
      if (hostType === HostType.APP && sendMessageRef?.current) {
        const lastSent = lastSentCoordsRef.current;
        const positionChanged = !lastSent || Math.abs(coords_0.x - lastSent.x) > MIN_WS_POSITION_DISTANCE || Math.abs(coords_0.y - lastSent.y) > MIN_WS_POSITION_DISTANCE || Math.abs(coords_0.z - lastSent.z) > MIN_WS_POSITION_DISTANCE;
        const bearingChanged = currentBearing !== null && (lastSentBearingRef.current === null || Math.abs(currentBearing - lastSentBearingRef.current) > MIN_WS_BEARING_CHANGE);
        const heartbeatDue = Date.now() - lastSentAtRef.current >= PLAYER_POSITION_HEARTBEAT_MS;
        if (positionChanged || bearingChanged || heartbeatDue) {
          sendMessageRef.current({
            type: 'player_position',
            coords: {
              x: coords_0.x,
              y: coords_0.y,
              z: coords_0.z
            },
            bearing: currentBearing
          });
          lastSentCoordsRef.current = {
            x: coords_0.x,
            y: coords_0.y,
            z: coords_0.z
          };
          lastSentBearingRef.current = currentBearing;
          lastSentAtRef.current = Date.now();
        }
      }
      if (shouldFollowPlayer && latLng_0.distanceTo(map.getCenter()) > MIN_DIRECTION_DISTANCE_M) {
        centerPlayer(latLng_0);
      }
    };
    const interval = setInterval(poll, UPDATE_INTERVAL_MS);
    void poll();
    return () => {
      clearInterval(interval);
      lastSentCoordsRef.current = null;
      lastSentBearingRef.current = null;
      lastSentAtRef.current = 0;
    };
  }, [map, deltas, shouldFollowPlayer, setCoords, enableLocation, setBearing, centerPlayer, disableLocation, titleSlug, fogRevealAtLatLng, hostType]);
  useEffect(() => {
    if (!shouldFollowPlayer || !playerCoords || !deltas) return;
    const latLng_1 = gameCoordsToLatLng(playerCoords, deltas.deltas);
    if (latLng_1) {
      centerPlayer(latLng_1);
    }
  }, [centerPlayer, deltas, playerCoords, shouldFollowPlayer]);
  const supportsLiveLocation = useMemo(() => {
    return Boolean(deltas);
  }, [deltas]);
  return {
    playerCoords,
    playerBearing,
    locationEnabled,
    teleportEnabled,
    followEnabled,
    supportsLiveLocation,
    setCoords,
    enableLocation,
    disableLocation,
    toggleFollow,
    setFollow,
    deltas
  };
}